// Project → Commissions tab. Hits /projects/:id/commissions which lists
// per-rep commission rows for the project's invoices. Read-only; payout runs
// from Finance › Commissions.
//
// Layout (other Business UI handoff W27, 2026-10-08): design table — rep,
// source (invoice + order), status, eligible base, rate, booked / billed
// margin, amount. Every figure is the commission engine's own; the only
// arithmetic here is formatting the stored rate × split as one percentage
// (unchanged from before).
import { useQuery } from "@tanstack/react-query";
import { Link, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/ui/StatusPill";
import { TABLE_HEAD, TABLE_WRAP, TD, TH, TROW } from "@/components/ui/RecordParts";
import type { ProjectDetailResponse } from "@/pages/ProjectDetailPage";

interface CommissionRow {
  id: string;
  invoiceId: string;
  invoiceNumber: number;
  orderId: string;
  orderNumber: number;
  repId: string;
  repName: string | null;
  repRole: string;
  basisAmount: string;
  rate: string;
  splitPercent: string;
  earnedAmount: string;
  status: string;
  estimatedAt: string | null;
  earnedAt: string | null;
  paidAt: string | null;
  paidAmount: string | null;
  payrollPeriod: string | null;
  // Order-level margins (CEO-corrected model 2026-07-21): BOOKED = expected
  // (rep-entered costs), BILLED = actual (from the bills). Cents are integer;
  // pct is hundredths-of-a-percent, or null when there's no revenue.
  revenueCents: number;
  // hasFinalInvoice: dropped — the API sends it but this tab never reads it.
  // Kept off the interface rather than carried as dead surface.
  hasBills: boolean;
  bookedMarginCents: number;
  bookedMarginPctHundredths: number | null;
  billedMarginCents: number;
  billedMarginPctHundredths: number | null;
}

interface CommissionsListResponse {
  rows: CommissionRow[];
}

function statusTone(
  s: string,
): "active" | "prospect" | "dormant" | "closed" | "warn" | "info" {
  switch (s) {
    case "estimated":
      return "info";
    case "earned":
      return "warn";
    case "paid":
      return "active";
    case "voided":
      return "closed";
    default:
      return "info";
  }
}

function statusLabel(s: string): string {
  switch (s) {
    case "estimated":
      return "Estimated";
    case "earned":
      return "Earned · not paid";
    case "paid":
      return "Paid";
    case "voided":
      return "Voided";
    default:
      return s.replace(/_/g, " ");
  }
}

function formatMoney(s: string | null): string {
  if (s === null) return "—";
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function formatPct(rate: string, split: string): string {
  const r = Number(rate);
  const s = Number(split);
  if (!Number.isFinite(r) || !Number.isFinite(s)) return "—";
  // rate stored as decimal (e.g. 0.05); split as percent (e.g. 100)
  const effective = r * (s / 100);
  return `${(effective * 100).toFixed(2)}%`;
}

/** Integer cents → "$1,234.56". */
function fmtCents(cents: number): string {
  return (cents / 100).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

/** Hundredths-of-a-percent → "42.35%"; null → em dash. */
function fmtPctH(h: number | null): string {
  return h == null ? "—" : `${(h / 100).toFixed(2)}%`;
}

function formatDate(s: string | null): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function ProjectCommissionsTab() {
  const data = useOutletContext<ProjectDetailResponse>();
  const projectId = data.project.id;

  const { data: list, isLoading, isError } = useQuery({
    queryKey: ["project", projectId, "commissions"],
    queryFn: () =>
      api<CommissionsListResponse>(`/projects/${projectId}/commissions`),
  });

  const rows = list?.rows ?? [];
  const count = (s: string) => rows.filter((r) => r.status === s).length;

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Commissions</h2>
        {!isLoading && !isError && rows.length > 0 && (
          <p className="text-xs text-muted">
            {count("estimated")} estimated · {count("earned")} earned · {count("paid")} paid
          </p>
        )}
      </div>

      {isLoading ? (
        <div className={`${TABLE_WRAP} space-y-3 p-5`} aria-label="Loading commissions">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-4 w-full animate-pulse rounded bg-canvas" />
          ))}
        </div>
      ) : isError ? (
        <div role="alert" className="rounded-md border border-risk/30 bg-risk-bg p-5 text-sm text-risk">
          We couldn't load this project's commissions. Nothing has changed — refresh to try again.
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-rule bg-white px-5 py-12 text-center">
          <p className="font-medium text-ink">No commissions yet.</p>
          <p className="mt-1 text-sm text-muted">
            A commission is estimated when an invoice is raised and becomes earned once the order is
            fully paid and every vendor bill is in.
          </p>
        </div>
      ) : (
        <>
          <div className={TABLE_WRAP} role="region" aria-label="Project commissions" tabIndex={0}>
            <table className="w-full text-left">
              <thead className={TABLE_HEAD}>
                <tr>
                  <th scope="col" className={TH}>Rep</th>
                  <th scope="col" className={`${TH} hidden md:table-cell`}>Source</th>
                  <th scope="col" className={TH}>Status</th>
                  <th scope="col" className={`${TH} hidden text-right lg:table-cell`}>Booked margin</th>
                  <th scope="col" className={`${TH} hidden text-right lg:table-cell`}>Billed margin</th>
                  <th scope="col" className={`${TH} hidden text-right sm:table-cell`}>Base</th>
                  <th scope="col" className={`${TH} hidden text-right sm:table-cell`}>Rate</th>
                  <th scope="col" className={`${TH} text-right`}>Amount</th>
                </tr>
              </thead>
              <tbody className="text-[13px]">
                {rows.map((c) => (
                  <tr key={c.id} className={TROW}>
                    <td className={TD}>
                      <span className="block font-medium text-ink">{c.repName ?? "—"}</span>
                      <span className="block text-[11px] capitalize text-muted">
                        {c.repRole.replace(/_/g, " ")}
                        <span className="md:hidden"> · Invoice #{c.invoiceNumber}</span>
                      </span>
                    </td>
                    <td className={`${TD} hidden md:table-cell`}>
                      <Link
                        to={`/projects/${projectId}/invoice/${c.invoiceId}`}
                        className="text-ink hover:underline"
                      >
                        Invoice #{c.invoiceNumber}
                      </Link>
                      <Link
                        to={`/projects/${projectId}/quotes/${c.orderId}`}
                        className="block text-[11px] text-muted hover:text-ink hover:underline"
                      >
                        Order #{c.orderNumber}
                      </Link>
                    </td>
                    <td className={TD}>
                      <StatusPill className="normal-case" tone={statusTone(c.status)}>{statusLabel(c.status)}</StatusPill>
                      <span className="mt-1 block text-[11px] text-muted">
                        {c.status === "paid"
                          ? `Paid ${formatDate(c.paidAt)}${c.payrollPeriod ? ` · period ${c.payrollPeriod}` : ""}`
                          : c.status === "earned"
                            ? `Earned ${formatDate(c.earnedAt)}`
                            : c.estimatedAt
                              ? `Since ${formatDate(c.estimatedAt)}`
                              : ""}
                      </span>
                    </td>
                    <td className={`${TD} hidden text-right tabular-nums text-muted lg:table-cell`}>
                      <span title="Expected margin — the order as sold (rep-entered costs)">
                        {fmtCents(c.bookedMarginCents)}
                      </span>
                      <span className="block text-[11px]">{fmtPctH(c.bookedMarginPctHundredths)}</span>
                    </td>
                    <td className={`${TD} hidden text-right tabular-nums text-muted lg:table-cell`}>
                      {c.hasBills || c.status !== "estimated" ? (
                        <>
                          <span title="Actual margin — from the bills (product + freight + service)">
                            {fmtCents(c.billedMarginCents)}
                          </span>
                          <span className="block text-[11px]">{fmtPctH(c.billedMarginPctHundredths)}</span>
                        </>
                      ) : (
                        // Never show a fake 100% for an invoiced-but-unbilled order
                        // still on ESTIMATE (commonsku does; we deliberately don't).
                        // Once earned, the billed margin is finalised and shown —
                        // even a genuine no-bill pure-service order.
                        <span className="text-[11px] italic">awaiting bills</span>
                      )}
                    </td>
                    <td className={`${TD} hidden text-right tabular-nums text-ink sm:table-cell`}>
                      {formatMoney(c.basisAmount)}
                    </td>
                    <td className={`${TD} hidden text-right tabular-nums text-ink sm:table-cell`}>
                      {formatPct(c.rate, c.splitPercent)}
                      {Number(c.splitPercent) !== 100 && (
                        <span className="block text-[11px] text-muted">{c.splitPercent}% split</span>
                      )}
                    </td>
                    <td className={`${TD} text-right font-medium tabular-nums text-ink`}>
                      {formatMoney(c.earnedAmount)}
                      {c.status === "paid" && c.paidAmount !== null && c.paidAmount !== c.earnedAmount && (
                        <span className="block text-[11px] font-normal text-muted">
                          Paid {formatMoney(c.paidAmount)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted">
            Base is what the rate applies to (margin or revenue, set per rate). Amounts are the commission
            engine&apos;s figures — estimated until the client has paid and every vendor bill is in.
          </p>
        </>
      )}
    </section>
  );
}
